import {
  counterText,
  counterValues,
  tileValues,
  type InsightsData,
  type ScreenKind,
} from './insights-values';
import { SCREEN } from './layout';

/**
 * Тот же экран «Статистика публикации», что в презентации, но картинкой.
 *
 * Презентация рисует его прямо в PDF (insights-screen.ts), а отчёт в Word
 * складывается из картинок — Word не умеет рисовать, он умеет вставлять.
 * Общего у двух путей ровно то, что и должно быть общим: шаблон-скриншот,
 * координаты из SCREEN и подбор цифр из insights-values.
 *
 * Рисуем в натуральную величину шаблона (923 px по ширине): в документе
 * картинка стоит в 7 см, это ~330 dpi — на печати не рассыпается.
 */

const TEMPLATE_URLS: Record<ScreenKind, string> = {
  reel: new URL('../../assets/images/insights/screen-reel.png', import.meta.url).href,
  post: new URL('../../assets/images/insights/screen-post.png', import.meta.url).href,
};

const FONT_URL = new URL('../../assets/fonts/SFPro-Semibold.ttf', import.meta.url).href;
/** Своё имя семейства: системный SF Pro в браузере не тот же файл. */
const FONT_FAMILY = 'ReportsInsightsSF';

const JPEG_QUALITY = 0.88;

let fontPromise: Promise<void> | null = null;

/** Шрифт экранов — один на страницу, грузится один раз. */
function ensureFont(): Promise<void> {
  fontPromise ??= (async () => {
    const face = new FontFace(FONT_FAMILY, `url(${FONT_URL})`);
    await face.load();
    document.fonts.add(face);
  })();
  return fontPromise;
}

const templates = new Map<ScreenKind, Promise<HTMLImageElement>>();

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Не удалось загрузить ${src}`));
    image.src = src;
  });
}

function loadTemplate(kind: ScreenKind): Promise<HTMLImageElement> {
  const cached = templates.get(kind);
  if (cached) return cached;
  const loading = loadImage(TEMPLATE_URLS[kind]);
  templates.set(kind, loading);
  return loading;
}

async function loadBlob(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  try {
    return await loadImage(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function roundedPath(
  ctx: CanvasRenderingContext2D,
  box: { x: number; y: number; w: number; h: number },
  radius: number,
): void {
  ctx.beginPath();
  ctx.roundRect(box.x, box.y, box.w, box.h, radius);
}

/** Превью вписывается в место публикации целиком и по центру, без обрезки. */
function drawContain(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  box: { x: number; y: number; w: number; h: number },
): void {
  const scale = Math.min(box.w / image.naturalWidth, box.h / image.naturalHeight);
  const width = image.naturalWidth * scale;
  const height = image.naturalHeight * scale;
  ctx.drawImage(image, box.x + (box.w - width) / 2, box.y + (box.h - height) / 2, width, height);
}

/** Картинка вместе с размером: по нему считается место в документе. */
export type ScreenImage = {
  blob: Blob;
  width: number;
  height: number;
};

export type InsightsScreenInput = {
  data: InsightsData;
  kind: ScreenKind;
  /** Превью публикации; null — место останется пустым, как в приложении. */
  thumb: Blob | null;
};

export async function renderInsightsScreen({
  data,
  kind,
  thumb,
}: InsightsScreenInput): Promise<ScreenImage> {
  const spec = kind === 'reel' ? SCREEN.reel : SCREEN.post;
  const [template, preview] = await Promise.all([
    loadTemplate(kind),
    thumb ? loadBlob(thumb) : Promise.resolve(null),
    ensureFont(),
  ]);

  const canvas = document.createElement('canvas');
  canvas.width = SCREEN.width;
  canvas.height = spec.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Браузер не дал холст для картинки');

  ctx.fillStyle = SCREEN.colors.background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(template, 0, 0, SCREEN.width, spec.height);

  const thumbBox = { x: spec.thumb.x, y: spec.thumb.y, w: spec.thumb.w, h: spec.thumb.h };
  ctx.save();
  roundedPath(ctx, thumbBox, spec.thumb.r);
  ctx.clip();
  if (spec.thumb.letterbox) {
    ctx.fillStyle = SCREEN.colors.letterbox;
    ctx.fillRect(thumbBox.x, thumbBox.y, thumbBox.w, thumbBox.h);
  }
  if (preview) drawContain(ctx, preview, thumbBox);
  ctx.restore();

  ctx.fillStyle = SCREEN.colors.text;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'center';
  ctx.font = `${SCREEN.counters.size}px "${FONT_FAMILY}"`;
  counterValues(data).forEach((value, i) => {
    if (value === null) return;
    ctx.fillText(counterText(value), SCREEN.counters.centers[i], spec.countersBaseline);
  });

  ctx.textAlign = 'left';
  const limit = SCREEN.tiles.w - 2 * SCREEN.tiles.padX;
  tileValues(data, kind).forEach((value, i) => {
    // Длинное число ужимается, чтобы не выйти за плитку, — как в приложении.
    let size = SCREEN.tiles.valueSize;
    ctx.font = `${size}px "${FONT_FAMILY}"`;
    while (size > 24 && ctx.measureText(value).width > limit) {
      size -= 0.5;
      ctx.font = `${size}px "${FONT_FAMILY}"`;
    }
    ctx.fillText(
      value,
      SCREEN.tiles.x[i % 2] + SCREEN.tiles.padX,
      spec.valueBaseline[Math.floor(i / 2)],
    );
  });

  // У рилса в четвёртой плитке приложение показывает подписки; их Graph API
  // для рилсов не отдаёт, поэтому подпись своя — по данным, которые есть.
  if (kind === 'reel') {
    ctx.fillStyle = SCREEN.colors.label;
    ctx.font = `${SCREEN.tiles.labelSize}px "${FONT_FAMILY}"`;
    ctx.fillText(
      'Взаимодействия',
      SCREEN.tiles.x[1] + SCREEN.tiles.padX,
      spec.labelBaseline[1],
    );
  }

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  if (!blob) throw new Error('Не удалось сохранить картинку экрана');
  return { blob, width: canvas.width, height: canvas.height };
}

/**
 * Скриншот, принесённый человеком, — к тому же виду: JPEG и известный
 * размер. Заодно снимается вопрос формата: в Word уезжает то, что Word
 * точно покажет, а не webp из мессенджера.
 */
export async function toScreenImage(file: Blob): Promise<ScreenImage> {
  const image = await loadBlob(file);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Браузер не дал холст для картинки');
  ctx.drawImage(image, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  if (!blob) throw new Error('Не удалось прочитать скриншот');
  return { blob, width: canvas.width, height: canvas.height };
}
